import { HubConnectionBuilder, HubConnectionState, LogLevel, type HubConnection } from "@microsoft/signalr";
import type { QueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { api, ApiError, type ChangeFeedDto } from "../api/client";

/**
 * Echtzeit nur als Hinweis: SignalR meldet "es gibt eine neue Revision", die App lädt per REST nach.
 * Nach einem Verbindungsabbruch werden Änderungen über /changes nachgeholt; ist der Cursor ungültig,
 * wird ein vollständiger Snapshot geladen (ARCHITECTURE §10.4).
 */
export type ConnectionState = "connecting" | "online" | "reconnecting" | "offline";

type Status = { state: ConnectionState; lastSync: string | null; revision: number };

let status: Status = { state: "connecting", lastSync: null, revision: 0 };
const listeners = new Set<() => void>();

function setStatus(patch: Partial<Status>) {
  status = { ...status, ...patch };
  listeners.forEach((l) => l());
}

export function useConnectionStatus(): Status {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => status,
  );
}

/** Snapshots melden ihre Revision; so weiß der Client, ab wann er nachholen muss. */
export function noteRevision(revision: number | string | undefined | null) {
  const r = Number(revision ?? 0);
  if (r > status.revision) {
    setStatus({ revision: r, lastSync: new Date().toISOString() });
  } else {
    setStatus({ lastSync: new Date().toISOString() });
  }
}

let connection: HubConnection | null = null;
let pollTimer: number | undefined;

async function catchUp(queryClient: QueryClient, useChangeFeed: boolean) {
  if (!useChangeFeed || status.revision === 0) {
    await queryClient.invalidateQueries();
    return;
  }
  try {
    const feed = await api<ChangeFeedDto>(`/api/v1/changes?after=${status.revision}`);
    if (feed.changes.length > 0 || Number(feed.revision) !== status.revision) {
      await queryClient.invalidateQueries();
    }
    noteRevision(feed.revision);
  } catch (e) {
    if (e instanceof ApiError && e.code === "resync_required") {
      setStatus({ revision: 0 });
      await queryClient.invalidateQueries();
    }
  }
}

export function startRealtime(queryClient: QueryClient, opts: { display?: boolean } = {}): () => void {
  stopRealtime();
  const useChangeFeed = !opts.display;
  connection = new HubConnectionBuilder()
    .withUrl("/hubs/family")
    .withAutomaticReconnect({ nextRetryDelayInMilliseconds: (ctx) => Math.min(30000, 1000 * 2 ** Math.min(ctx.previousRetryCount, 5)) })
    .configureLogging(LogLevel.Warning)
    .build();

  connection.on("familyChanged", (payload: { revision: number }) => {
    if (Number(payload.revision) > status.revision) {
      void queryClient.invalidateQueries();
    }
  });
  connection.on("inputUpdated", () => void queryClient.invalidateQueries({ queryKey: ["inputs"] }));
  connection.onreconnecting(() => setStatus({ state: "reconnecting" }));
  connection.onreconnected(() => {
    setStatus({ state: "online" });
    void catchUp(queryClient, useChangeFeed);
  });
  connection.onclose(() => setStatus({ state: "offline" }));

  const start = async () => {
    if (!connection || connection.state !== HubConnectionState.Disconnected) return;
    setStatus({ state: "connecting" });
    try {
      await connection.start();
      setStatus({ state: "online" });
      await catchUp(queryClient, useChangeFeed);
    } catch {
      setStatus({ state: "offline" });
    }
  };
  void start();

  // Absicherung: periodisch erneut verbinden bzw. nachladen (SignalR garantiert keine lückenlose Zustellung).
  pollTimer = window.setInterval(() => {
    if (connection?.state === HubConnectionState.Disconnected) {
      void start();
    } else if (connection?.state === HubConnectionState.Connected) {
      void catchUp(queryClient, useChangeFeed);
    }
  }, opts.display ? 60000 : 120000);

  const onOnline = () => void start();
  window.addEventListener("online", onOnline);
  return () => {
    window.removeEventListener("online", onOnline);
    stopRealtime();
  };
}

export function stopRealtime() {
  if (pollTimer) window.clearInterval(pollTimer);
  pollTimer = undefined;
  void connection?.stop();
  connection = null;
}
